import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { 
  Truck, 
  MapPin, 
  Clock, 
  Phone,
  ExternalLink,
  RefreshCw,
  Car,
  User,
  Loader2,
  CheckCircle,
  AlertCircle,
  Calendar,
  Package,
  ChevronRight,
  X,
  Navigation,
  FileText,
  Euro
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Mapeo de estados internos a estados simplificados para el cliente
const getSimplifiedStatus = (status) => {
  if (!status) return 'sin_reservar';
  
  const statusLower = status.toLowerCase();
  
  // Cancelados
  if (statusLower.includes('cancelado') || statusLower === 'cancelled') {
    return 'cancelado';
  }
  
  // Entregado
  if (statusLower === 'delivered' || statusLower === 'completed') {
    return 'entregado';
  }
  
  // En Proceso (en ruta)
  if (statusLower === 'in_transit' || statusLower === 'in_progress') {
    return 'en_proceso';
  }
  
  // Reservado (conductor asignado)
  if (statusLower === 'assigned' || statusLower === 'reserved' || statusLower === 'por_asignar') {
    return 'reservado';
  }
  
  // Sin reservar (pendiente, confirmado, etc.)
  return 'sin_reservar';
};

const STATUS_CONFIG = {
  sin_reservar: {
    label: 'Sin Reservar',
    shortLabel: 'Sin Reservar',
    color: 'bg-amber-100 text-amber-800 border-amber-300',
    icon: AlertCircle,
    iconColor: 'text-amber-500',
    description: 'Esperando que un conductor acepte el servicio'
  },
  reservado: {
    label: 'Reservado',
    shortLabel: 'Reservado',
    color: 'bg-blue-100 text-blue-800 border-blue-300',
    icon: User,
    iconColor: 'text-blue-500',
    description: 'Un conductor ha aceptado el servicio'
  },
  en_proceso: {
    label: 'En Proceso',
    shortLabel: 'En Proceso',
    color: 'bg-orange-100 text-orange-800 border-orange-300',
    icon: Truck,
    iconColor: 'text-orange-500',
    description: 'El vehículo está siendo transportado'
  },
  entregado: {
    label: 'Entregado',
    shortLabel: 'Entregado',
    color: 'bg-green-100 text-green-800 border-green-300',
    icon: CheckCircle,
    iconColor: 'text-green-500',
    description: 'Servicio completado'
  },
  cancelado: {
    label: 'Cancelado',
    shortLabel: 'Cancelado',
    color: 'bg-red-100 text-red-800 border-red-300',
    icon: X,
    iconColor: 'text-red-500',
    description: 'Servicio cancelado'
  }
};

const ClientDeliveriesTab = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  const [activeFilter, setActiveFilter] = useState('todos');
  const [detailModalOpen, setDetailModalOpen] = useState(false);

  const fetchServices = useCallback(async () => {
    try {
      const token = localStorage.getItem('session_token');
      const response = await fetch(`${BACKEND_URL}/api/orders`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setServices(data || []);
        setLastUpdate(new Date());
      }
    } catch (error) {
      console.error('Error fetching services:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchServices();
    
    // Auto-refresh every 30 seconds
    const interval = setInterval(() => {
      fetchServices();
    }, 30000);
    
    return () => clearInterval(interval);
  }, [fetchServices]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchServices();
  };

  // Agrupar servicios por estado simplificado
  const groupedServices = {
    sin_reservar: services.filter(s => getSimplifiedStatus(s.status) === 'sin_reservar'),
    reservado: services.filter(s => getSimplifiedStatus(s.status) === 'reservado'),
    en_proceso: services.filter(s => getSimplifiedStatus(s.status) === 'en_proceso'),
    entregado: services.filter(s => getSimplifiedStatus(s.status) === 'entregado'),
    cancelado: services.filter(s => getSimplifiedStatus(s.status) === 'cancelado')
  };

  // Servicios activos (no cancelados ni entregados)
  const activeCount = groupedServices.sin_reservar.length + 
                      groupedServices.reservado.length + 
                      groupedServices.en_proceso.length;

  // Filtrar servicios según el filtro activo
  const getFilteredServices = () => {
    if (activeFilter === 'todos') {
      // Mostrar todos excepto cancelados, ordenados por estado
      return [
        ...groupedServices.en_proceso,
        ...groupedServices.reservado,
        ...groupedServices.sin_reservar,
        ...groupedServices.entregado.slice(0, 10) // Solo últimos 10 entregados
      ];
    }
    if (activeFilter === 'activos') {
      return [
        ...groupedServices.en_proceso,
        ...groupedServices.reservado,
        ...groupedServices.sin_reservar
      ];
    }
    return groupedServices[activeFilter] || [];
  };

  const filteredServices = getFilteredServices();

  const formatDate = (dateString) => {
    if (!dateString) return 'No especificada';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', { 
      day: '2-digit', 
      month: 'short',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'No especificada';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', { 
      day: '2-digit', 
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const openServiceDetail = (service) => {
    setSelectedService(service);
    setDetailModalOpen(true);
  };

  const getServiceTypeLabel = (type) => {
    const types = {
      'delivery': 'Entrega',
      'bc_service': 'B-C',
      'tradein': 'Trade-In',
      'representacion': 'Representación'
    };
    return types[type] || type || 'Entrega';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-green-600" />
        <span className="ml-2 text-gray-600">Cargando servicios...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="client-tracking-tab">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Package className="w-6 h-6 text-[#0a3042]" />
            Mis Servicios
          </h2>
          <p className="text-gray-600 mt-1">
            Seguimiento de tus transportes de vehículos
          </p>
        </div>
        
        <Button 
          variant="outline" 
          size="sm" 
          onClick={handleRefresh}
          disabled={refreshing}
          className="shrink-0"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Actualizar
        </Button>
      </div>

      {/* Stats Cards - Estados Simplificados */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {/* Sin Reservar */}
        <button
          onClick={() => setActiveFilter(activeFilter === 'sin_reservar' ? 'todos' : 'sin_reservar')}
          className={`p-4 rounded-xl border-2 transition-all text-left ${
            activeFilter === 'sin_reservar' 
              ? 'border-amber-400 bg-amber-50 shadow-md' 
              : 'border-gray-200 bg-white hover:border-amber-300 hover:bg-amber-50/50'
          }`}
        >
          <div className="flex items-center justify-between mb-2">
            <AlertCircle className="w-5 h-5 text-amber-500" />
            <span className="text-2xl font-bold text-amber-600">{groupedServices.sin_reservar.length}</span>
          </div>
          <p className="text-xs font-medium text-gray-600">Sin Reservar</p>
        </button>

        {/* Reservado */}
        <button
          onClick={() => setActiveFilter(activeFilter === 'reservado' ? 'todos' : 'reservado')}
          className={`p-4 rounded-xl border-2 transition-all text-left ${
            activeFilter === 'reservado' 
              ? 'border-blue-400 bg-blue-50 shadow-md' 
              : 'border-gray-200 bg-white hover:border-blue-300 hover:bg-blue-50/50'
          }`}
        >
          <div className="flex items-center justify-between mb-2">
            <User className="w-5 h-5 text-blue-500" />
            <span className="text-2xl font-bold text-blue-600">{groupedServices.reservado.length}</span>
          </div>
          <p className="text-xs font-medium text-gray-600">Reservado</p>
        </button>

        {/* En Proceso */}
        <button
          onClick={() => setActiveFilter(activeFilter === 'en_proceso' ? 'todos' : 'en_proceso')}
          className={`p-4 rounded-xl border-2 transition-all text-left ${
            activeFilter === 'en_proceso' 
              ? 'border-orange-400 bg-orange-50 shadow-md' 
              : 'border-gray-200 bg-white hover:border-orange-300 hover:bg-orange-50/50'
          }`}
        >
          <div className="flex items-center justify-between mb-2">
            <Truck className="w-5 h-5 text-orange-500" />
            <span className="text-2xl font-bold text-orange-600">{groupedServices.en_proceso.length}</span>
          </div>
          <p className="text-xs font-medium text-gray-600">En Proceso</p>
        </button>

        {/* Entregado */}
        <button
          onClick={() => setActiveFilter(activeFilter === 'entregado' ? 'todos' : 'entregado')}
          className={`p-4 rounded-xl border-2 transition-all text-left ${
            activeFilter === 'entregado' 
              ? 'border-green-400 bg-green-50 shadow-md' 
              : 'border-gray-200 bg-white hover:border-green-300 hover:bg-green-50/50'
          }`}
        >
          <div className="flex items-center justify-between mb-2">
            <CheckCircle className="w-5 h-5 text-green-500" />
            <span className="text-2xl font-bold text-green-600">{groupedServices.entregado.length}</span>
          </div>
          <p className="text-xs font-medium text-gray-600">Entregado</p>
        </button>

        {/* Cancelado (solo si hay) */}
        {groupedServices.cancelado.length > 0 && (
          <button
            onClick={() => setActiveFilter(activeFilter === 'cancelado' ? 'todos' : 'cancelado')}
            className={`p-4 rounded-xl border-2 transition-all text-left ${
              activeFilter === 'cancelado' 
                ? 'border-red-400 bg-red-50 shadow-md' 
                : 'border-gray-200 bg-white hover:border-red-300 hover:bg-red-50/50'
            }`}
          >
            <div className="flex items-center justify-between mb-2">
              <X className="w-5 h-5 text-red-500" />
              <span className="text-2xl font-bold text-red-600">{groupedServices.cancelado.length}</span>
            </div>
            <p className="text-xs font-medium text-gray-600">Cancelado</p>
          </button>
        )}
      </div>

      {/* Filter indicator */}
      {activeFilter !== 'todos' && (
        <div className="flex items-center gap-2">
          <Badge variant="outline" className={STATUS_CONFIG[activeFilter]?.color || 'bg-gray-100'}>
            Mostrando: {STATUS_CONFIG[activeFilter]?.label || 'Todos'}
          </Badge>
          <Button variant="ghost" size="sm" onClick={() => setActiveFilter('todos')}>
            Ver todos
          </Button>
        </div>
      )}

      {/* Services List */}
      <div className="space-y-3">
        {filteredServices.length === 0 ? (
          <Card>
            <CardContent className="py-12">
              <div className="text-center">
                <Package className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900">No hay servicios</h3>
                <p className="text-gray-500 mt-1">
                  {activeFilter !== 'todos' 
                    ? `No tienes servicios con estado "${STATUS_CONFIG[activeFilter]?.label}"`
                    : 'Tus servicios de transporte aparecerán aquí'
                  }
                </p>
              </div>
            </CardContent>
          </Card>
        ) : (
          filteredServices.map((service) => {
            const simplifiedStatus = getSimplifiedStatus(service.status);
            const statusConfig = STATUS_CONFIG[simplifiedStatus];
            const StatusIcon = statusConfig.icon;

            return (
              <Card 
                key={service.id} 
                className="cursor-pointer hover:shadow-lg transition-all border-l-4"
                style={{ borderLeftColor: simplifiedStatus === 'sin_reservar' ? '#f59e0b' : 
                                          simplifiedStatus === 'reservado' ? '#3b82f6' :
                                          simplifiedStatus === 'en_proceso' ? '#f97316' :
                                          simplifiedStatus === 'entregado' ? '#22c55e' : '#ef4444' }}
                onClick={() => openServiceDetail(service)}
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    {/* Status Icon */}
                    <div className={`p-3 rounded-xl ${statusConfig.color.split(' ')[0]}`}>
                      <StatusIcon className={`w-6 h-6 ${statusConfig.iconColor}`} />
                    </div>

                    {/* Main Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-bold text-lg text-gray-900">
                          {service.car_plate || 'Sin matrícula'}
                        </span>
                        <Badge className={`${statusConfig.color} border`}>
                          {statusConfig.shortLabel}
                        </Badge>
                        <Badge variant="outline" className="text-xs">
                          {getServiceTypeLabel(service.service_type)}
                        </Badge>
                      </div>
                      
                      <p className="text-sm text-gray-600 mt-1">
                        {service.car_brand} {service.car_model}
                      </p>

                      {/* Route */}
                      <div className="flex items-center gap-2 mt-2 text-sm text-gray-500">
                        <MapPin className="w-4 h-4 text-green-500 shrink-0" />
                        <span className="truncate">{service.pickup_address || 'Origen'}</span>
                        <span className="text-gray-300">→</span>
                        <MapPin className="w-4 h-4 text-red-500 shrink-0" />
                        <span className="truncate">{service.delivery_address || 'Destino'}</span>
                      </div>

                      {/* Driver info if reserved */}
                      {(simplifiedStatus === 'reservado' || simplifiedStatus === 'en_proceso') && service.driver_name && (
                        <div className="flex items-center gap-2 mt-2 text-sm">
                          <User className="w-4 h-4 text-blue-500" />
                          <span className="font-medium text-blue-700">{service.driver_name}</span>
                          {service.driver_phone && (
                            <a 
                              href={`tel:${service.driver_phone}`} 
                              className="flex items-center gap-1 text-green-600 hover:underline"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Phone className="w-3 h-3" />
                              {service.driver_phone}
                            </a>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Right side - Date & Arrow */}
                    <div className="text-right shrink-0">
                      <div className="flex items-center gap-1 text-sm text-gray-500 mb-1">
                        <Calendar className="w-4 h-4" />
                        <span>{formatDate(service.pickup_date_from || service.created_at)}</span>
                      </div>
                      {service.total_price && (
                        <p className="text-sm font-semibold text-gray-700">
                          {service.total_price.toFixed(2)}€
                        </p>
                      )}
                      <ChevronRight className="w-5 h-5 text-gray-400 mt-2 ml-auto" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>

      {/* Last Update */}
      {lastUpdate && (
        <p className="text-sm text-gray-500 text-center">
          Última actualización: {lastUpdate.toLocaleTimeString('es-ES')}
        </p>
      )}

      {/* Service Detail Modal */}
      <Dialog open={detailModalOpen} onOpenChange={setDetailModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {selectedService && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-3">
                  <Car className="w-6 h-6" />
                  <span>{selectedService.car_plate}</span>
                  <Badge className={STATUS_CONFIG[getSimplifiedStatus(selectedService.status)]?.color}>
                    {STATUS_CONFIG[getSimplifiedStatus(selectedService.status)]?.label}
                  </Badge>
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-6 mt-4">
                {/* Vehicle Info */}
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <Car className="w-4 h-4" />
                    Vehículo
                  </h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">Marca:</span>
                      <p className="font-medium">{selectedService.car_brand || '-'}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Modelo:</span>
                      <p className="font-medium">{selectedService.car_model || '-'}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Matrícula:</span>
                      <p className="font-medium">{selectedService.car_plate || '-'}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Tipo de Servicio:</span>
                      <p className="font-medium">{getServiceTypeLabel(selectedService.service_type)}</p>
                    </div>
                  </div>
                </div>

                {/* Route Info */}
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <Navigation className="w-4 h-4" />
                    Ruta
                  </h4>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <div className="w-3 h-3 rounded-full bg-green-500 mt-1.5 shrink-0"></div>
                      <div>
                        <p className="text-xs text-gray-500">Recogida</p>
                        <p className="font-medium">{selectedService.pickup_address || '-'}</p>
                        {selectedService.pickup_date_from && (
                          <p className="text-sm text-gray-600 flex items-center gap-1 mt-1">
                            <Clock className="w-3 h-3" />
                            {formatDateTime(selectedService.pickup_date_from)}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-3 h-3 rounded-full bg-red-500 mt-1.5 shrink-0"></div>
                      <div>
                        <p className="text-xs text-gray-500">Entrega</p>
                        <p className="font-medium">{selectedService.delivery_address || '-'}</p>
                        {selectedService.delivery_date_from && (
                          <p className="text-sm text-gray-600 flex items-center gap-1 mt-1">
                            <Clock className="w-3 h-3" />
                            {formatDateTime(selectedService.delivery_date_from)}
                          </p>
                        )}
                      </div>
                    </div>
                    {selectedService.distance_km && (
                      <p className="text-sm text-gray-600 mt-2">
                        Distancia: <strong>{selectedService.distance_km} km</strong>
                      </p>
                    )}
                  </div>
                </div>

                {/* Driver Info (if assigned) */}
                {selectedService.driver_name && (
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <h4 className="font-semibold text-blue-700 mb-3 flex items-center gap-2">
                      <User className="w-4 h-4" />
                      Conductor Asignado
                    </h4>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-lg">{selectedService.driver_name}</p>
                        {selectedService.driver_phone && (
                          <a 
                            href={`tel:${selectedService.driver_phone}`}
                            className="text-blue-600 hover:underline flex items-center gap-1 mt-1"
                          >
                            <Phone className="w-4 h-4" />
                            {selectedService.driver_phone}
                          </a>
                        )}
                      </div>
                      {selectedService.tracking_url && (
                        <a
                          href={selectedService.tracking_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                        >
                          <ExternalLink className="w-4 h-4" />
                          Ver Tracking
                        </a>
                      )}
                    </div>
                  </div>
                )}

                {/* Pricing */}
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <Euro className="w-4 h-4" />
                    Precio
                  </h4>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Total</span>
                    <span className="text-2xl font-bold text-[#0a3042]">
                      {(selectedService.total_price || selectedService.price || 0).toFixed(2)}€
                    </span>
                  </div>
                  {selectedService.payment_status && (
                    <div className="mt-2 flex items-center justify-between text-sm">
                      <span className="text-gray-500">Estado del pago:</span>
                      <Badge variant="outline" className={
                        selectedService.payment_status === 'paid' ? 'bg-green-100 text-green-700' :
                        selectedService.payment_status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-gray-100 text-gray-700'
                      }>
                        {selectedService.payment_status === 'paid' ? 'Pagado' :
                         selectedService.payment_status === 'pending' ? 'Pendiente' : 
                         selectedService.payment_status}
                      </Badge>
                    </div>
                  )}
                </div>

                {/* Notes */}
                {(selectedService.notes || selectedService.first_vehicle_notes || selectedService.second_vehicle_notes) && (
                  <div className="p-4 bg-amber-50 rounded-lg">
                    <h4 className="font-semibold text-amber-700 mb-2 flex items-center gap-2">
                      <FileText className="w-4 h-4" />
                      Notas
                    </h4>
                    {selectedService.notes && (
                      <p className="text-sm text-amber-900">{selectedService.notes}</p>
                    )}
                    {selectedService.first_vehicle_notes && (
                      <p className="text-sm text-amber-900 mt-2">
                        <strong>Vehículo 1:</strong> {selectedService.first_vehicle_notes}
                      </p>
                    )}
                    {selectedService.second_vehicle_notes && (
                      <p className="text-sm text-amber-900 mt-2">
                        <strong>Vehículo 2:</strong> {selectedService.second_vehicle_notes}
                      </p>
                    )}
                  </div>
                )}

                {/* Timestamps */}
                <div className="text-xs text-gray-400 pt-2 border-t">
                  <p>Creado: {formatDateTime(selectedService.created_at)}</p>
                  {selectedService.updated_at && (
                    <p>Actualizado: {formatDateTime(selectedService.updated_at)}</p>
                  )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientDeliveriesTab;
